import { FormItem } from 'components';
import { Button } from 'components/Button';
import OTPInput from 'components/OTPInput';
import { useFormik } from 'formik';
import {
  ILocalStorageSignup,
  useLocalStorageSignup,
} from 'hooks/useLocalStorageSignup';
import { useQueryParams } from 'hooks/useQueryParams';
import React, { useCallback, useState } from 'react';
import { authenticationService, mainService } from 'services/driverPortal';
import {
  ILoginWithPhoneRequest,
  ISignUpRequest,
  LoginType,
} from 'services/driverPortal/AuthenticationService.interface';
import { IResponseError } from 'utils/transformResponseError';
import { theme } from '../../../configs/styled';
import CountDown from '../components/CountDown';
import TextBold from '../components/TextBold';
import TextRegular from '../components/TextRegular';
import Title from '../components/Title';

function disableButtonRequestOTP(
  otpCountdownSeconds: number,
  disableRequestOTP: boolean,
) {
  if (otpCountdownSeconds === 0) return false;
  return disableRequestOTP;
}

function defaultOTPExpiredAt() {
  const t = new Date().setSeconds(0);
  return new Date(t).toISOString();
}

function buildLoginWithPhonePayload(
  phone: string,
  otpCode: string,
  otpRefCode: string,
): ILoginWithPhoneRequest {
  return { type: LoginType.PHONE, phone, otpCode, otpRefCode };
}

function buildSignUpPayload(
  signup: ILocalStorageSignup,
  otpCode: string,
  otpRefCode: string,
): ISignUpRequest {
  return {
    ...signup,
    provinceNameArr: signup.provinceNameArr || [],
    districtNameArr: signup.districtNameArr || [],
    vehicleTypeArr: signup.vehicleTypeArr || [],
    businessTypeArr: signup.businessTypeArr || [],
    otpCode,
    otpRefCode,
  };
}

// async function handlerSubmitOTPForm(
//   signup: ILocalStorageSignup,
//   otpValues: string,
//   otpRefCode: string,
// ) {
//   const { firstname = '', phone } = signup;
//   const isSignupSubmit = firstname.length > 0;
//   if (isSignupSubmit) {
//     const signupPayload = buildSignUpPayload(signup, otpValues, otpRefCode);
//     await authenticationService.otpVerify(signupPayload);
//   } else {
//     const loginPayload = buildLoginWithPhonePayload(
//       phone,
//       otpValues,
//       otpRefCode,
//     );
//     await authenticationService.login(loginPayload);
//   }
// }

async function handlerSubmitOTPForm(
  signup: ILocalStorageSignup,
  otpValues: string,
  otpRefCode: string,
): Promise<void> {
  const signupPayload = buildSignUpPayload(signup, otpValues, otpRefCode);
  await authenticationService.otpVerify(signupPayload);
}

const OTPForm = () => {
  const { search, pushUrl } = useQueryParams();
  const sessionSignup = useLocalStorageSignup();
  const signup = sessionSignup.getValues();

  const otpLength = Number(signup?.otpDigit) || 6;
  const initialOtpExpiredAt = signup?.expired || defaultOTPExpiredAt();
  const initialOtpRefCode = signup?.otpRefCode || '';
  const phone = signup?.phone || '';

  if (phone.length === 0) {
    pushUrl('/login', search);
  }

  const goToMainPage = () => {
    pushUrl('/', search);
  };

  const [disableSubmit, setDisableSubmit] = useState(true);
  const [disableRequestOTP, setDisableRequestOTP] = useState(true);
  const [visibleRequestOTPText, setVisibleRequestOTPText] = useState(true);
  const [resetOtpValues, setResetOtpValues] = useState(false);
  const [otpCountDownSeconds, setOtpCountDownSeconds] = useState(0);
  const [otpExpiredAt, setOtpExpiredAt] = useState(initialOtpExpiredAt);
  const [otpRefCode, setOtpRefCode] = useState(initialOtpRefCode);
  const [resendLoading, setResendLoading] = useState(false);

  const formik = useFormik({
    initialValues: { otpCode: '' },
    onSubmit: async formValues => {
      try {
        const { otpCode } = formValues;
        signup && (await handlerSubmitOTPForm(signup, otpCode, otpRefCode));
        sessionSignup.clearValues();

        goToMainPage();
      } catch (error) {
        const _error = error as IResponseError;
        const errorMessage =
          _error?.data?.error || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';

        let messageString = '';

        if (typeof errorMessage === 'string') {
          if (errorMessage === 'Invalid OTP') {
            messageString = 'รหัส OTP ไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง';
          } else {
            messageString = errorMessage;
          }
        } else if (errorMessage?.message === 'Invalid OTP') {
          messageString = 'รหัส OTP ไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง';
        } else {
          messageString =
            errorMessage?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';
        }

        const resultError = { otpCode: messageString };
        formik.setErrors(resultError);
        setResetOtpValues(true);

        // const _error = transformResponseError(error as IResponseError);
        // _error.isValidationError && formik.setErrors(_error.errors);
        // setResetOtpValues(true);
      }
    },
  });

  const updateStatusCode = async () => {
    try {
      const userId = authenticationService.getUserToken()?.userId ?? '';
      await mainService.updateStatusCode(userId, 'waiting_rider_apply');
    } catch (err) {}
  };

  const handleClickResendOTP = async () => {
    if (resendLoading) return;

    formik.resetForm({});
    setDisableSubmit(false);
    setResetOtpValues(true);
    setDisableRequestOTP(true);
    setVisibleRequestOTPText(true);

    setResendLoading(true);

    try {
      const response = await authenticationService.requestOTP({ phone });
      const otpExpireTime = response.expired || response.expiredAt;

      if (response.otpRefcode) {
        setOtpRefCode(response.otpRefcode);
      }
      if (otpExpireTime) {
        setOtpExpiredAt(otpExpireTime);
      }

      sessionSignup.setValues({
        ...signup,
        otpRefCode: response.otpRefcode || '',
        expired: otpExpireTime || '',
      } as ILocalStorageSignup);
    } catch (error) {
      const _error = error as IResponseError;
      const errorMessage =
        _error?.data?.error || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';

      let messageString = '';

      if (typeof errorMessage === 'string') {
        if (errorMessage === 'Invalid OTP') {
          messageString = 'รหัส OTP ไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง';
        } else {
          messageString = errorMessage;
        }
      } else if (errorMessage?.message === 'Invalid OTP') {
        messageString = 'รหัส OTP ไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง';
      } else {
        messageString =
          errorMessage?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';
      }
      const resultError = { otpCode: messageString };
      formik.setErrors(resultError);

      // const _error = transformResponseError(error as IResponseError);
      // _error.isValidationError && formik.setErrors(_error.errors);
      setDisableRequestOTP(false);
    } finally {
      setResendLoading(false);
    }
  };

  const handleOtpValueChange = (otpCode: string): void => {
    if (formik.errors.otpCode) formik.setErrors({});
    setResetOtpValues(false);
    formik.setFieldValue('otpCode', otpCode);
    setDisableSubmit(otpCode.length !== otpLength);
  };

  const handleCountDownChange = useCallback(
    (seconds: number) => {
      setOtpCountDownSeconds(seconds);
      if (seconds === 0) {
        setVisibleRequestOTPText(false);
        setDisableRequestOTP(false);
      }
    },
    [disableRequestOTP],
  );

  return (
    <form onSubmit={formik.handleSubmit}>
      <Title>ยืนยันด้วยรหัส OTP</Title>
      <TextRegular>
        กรุณากรอกรหัส OTP เป็นตัวเลข {otpLength} หลักที่ส่งไปยังเบอร์โทรศัพท์
        <br />
        <TextBold>{phone}</TextBold>
        เลขที่อ้างอิง
        <TextBold>{otpRefCode}</TextBold>
      </TextRegular>
      <FormItem
        style={{ textAlign: 'center', margin: '1.5rem auto' }}
        validateStatus={formik.errors.otpCode ? 'error' : ''}
        help={formik.errors.otpCode}
      >
        <OTPInput
          autoFocus
          isNumberInput
          resetValues={resetOtpValues}
          length={otpLength}
          className="otpContainer"
          inputClassName="otpInput"
          onChangeOTP={handleOtpValueChange}
        />
      </FormItem>

      <div style={{ textAlign: 'center' }}>
        {visibleRequestOTPText && (
          <div>
            <TextRegular>กดส่งรหัสใหม่อีกครั้ง หากไม่ได้รับภายใน:</TextRegular>
            <TextBold>
              <CountDown
                onChangeSeconds={handleCountDownChange}
                expiredAt={otpExpiredAt}
              />
              วินาที
            </TextBold>
          </div>
        )}

        <Button
          // disabled={disableButtonRequestOTP(
          //   otpCountDownSeconds,
          //   disableRequestOTP,
          // )}
          disabled={
            resendLoading ||
            disableButtonRequestOTP(otpCountDownSeconds, disableRequestOTP)
          }
          loading={resendLoading}
          style={{ marginTop: theme.spacing.sm }}
          size="middle"
          onClick={handleClickResendOTP}
        >
          ส่งรหัส OTP อีกครั้ง
        </Button>
      </div>

      <Button
        // disabled={disableSubmit || otpCountDownSeconds === 0}
        // type={
        //   disableSubmit || otpCountDownSeconds === 0 ? 'default' : 'primary'
        // }
        style={{ marginTop: theme.spacing.xxxl }}
        block
        size="large"
        onClick={() => {
          formik.handleSubmit();
        }}
        type={
          disableSubmit ||
          formik.isSubmitting ||
          resendLoading ||
          otpCountDownSeconds === 0
            ? 'default'
            : 'primary'
        }
        disabled={
          disableSubmit ||
          formik.isSubmitting ||
          resendLoading ||
          otpCountDownSeconds === 0
        }
      >
        {formik.isSubmitting ? 'กำลังยืนยัน...' : 'ยืนยัน'}
      </Button>
    </form>
  );
};

export default OTPForm;
